// src/admin/PrintClassList.js
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import { firestore } from '../firebaseConfig';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import classRoomsData from '../data/classRooms.json';
import './PrintClassList.css';

const PrintClassList = () => {
  const {
    currentYearId,
    studentsData,
    classMap,
    catechistsData
  } = useContext(StudentContext);

  const outletContext = useOutletContext();
  const sidebarSelectedClass = outletContext?.selectedClassForOutlet || 'all';

  const [activeClassId, setActiveClassId] = useState('');
  const [currentYearName, setCurrentYearName] = useState('');
  const [prevClassMap, setPrevClassMap] = useState({});
  const [loadingPrevYear, setLoadingPrevYear] = useState(false);

  useEffect(() => {
    if (sidebarSelectedClass && sidebarSelectedClass !== 'all') {
      setActiveClassId(sidebarSelectedClass);
    } else if (!activeClassId) {
      const firstClassKey = Object.keys(classMap)[0];
      if (firstClassKey) setActiveClassId(firstClassKey);
    }
  }, [sidebarSelectedClass, classMap, activeClassId]);

  useEffect(() => {
    const fetchAcademicYearsAndPrevEnrollments = async () => {
      try {
        setLoadingPrevYear(true);
        const qYears = query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc'));
        const yearsSnap = await getDocs(qYears);
        const yearsList = yearsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const curYear = yearsList.find(y => y.id === currentYearId || y.isCurrent) || yearsList[0];
        if (curYear) {
          setCurrentYearName(curYear.name || curYear.id);
        }

        const curIndex = yearsList.findIndex(y => y.id === curYear?.id);
        const prevYear = (curIndex !== -1 && curIndex + 1 < yearsList.length) ? yearsList[curIndex + 1] : null;

        if (prevYear) {
          const prevClassesSnap = await getDocs(collection(firestore, `academic_years/${prevYear.id}/classes`));
          const prevClassesNameMap = {};
          prevClassesSnap.docs.forEach(d => {
            prevClassesNameMap[d.id] = d.data().name || d.id;
          });

          const prevEnrollSnap = await getDocs(collection(firestore, `academic_years/${prevYear.id}/enrollments`));
          const studentPrevClassMapping = {};
          prevEnrollSnap.docs.forEach(d => {
            const enrData = d.data();
            const sId = enrData.studentId || d.id;
            if (enrData.classId) {
              studentPrevClassMapping[sId] = prevClassesNameMap[enrData.classId] || enrData.classId;
            }
          });

          setPrevClassMap(studentPrevClassMapping);
        }
      } catch (err) {
        console.error('Lỗi khi tải thông tin lớp niên khóa trước:', err);
      } finally {
        setLoadingPrevYear(false);
      }
    };

    fetchAcademicYearsAndPrevEnrollments();
  }, [currentYearId]);

  const currentClassName = classMap[activeClassId] || activeClassId || 'Chưa chọn lớp';

  const roomInfo = useMemo(() => {
    if (!activeClassId) return null;
    return classRoomsData.find(
      r => r.classId === activeClassId ||
           (r.className && r.className.trim().toLowerCase() === currentClassName.trim().toLowerCase())
    );
  }, [activeClassId, currentClassName]);

  const roomName = roomInfo?.room || 'Chưa xếp phòng';
  const groupName = roomInfo?.block || 'Chung';

  // 🌟 HÀM XÁC ĐỊNH CLASS MÀU THEO TỪNG NGÀNH
  const groupColorClass = useMemo(() => {
    const norm = (groupName || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    if (norm.includes('chien')) return 'banner-chien-con';
    if (norm.includes('au')) return 'banner-au';
    if (norm.includes('thieu')) return 'banner-thieu';
    if (norm.includes('nghia')) return 'banner-nghia';
    if (norm.includes('hiep')) return 'banner-hiep-si';
    return 'banner-default';
  }, [groupName]);

  const assignedTeachers = useMemo(() => {
    if (!currentClassName || !catechistsData) return [];

    const matched = catechistsData.filter(c => {
      if ((c.status || 'active') === 'inactive') return false;
      return c.className === currentClassName || c.className === activeClassId;
    });

    matched.sort((a, b) => {
      const aIsTuSi = (a.religious || 'Giáo lý viên') === 'Tu Sĩ' ? 0 : 1;
      const bIsTuSi = (b.religious || 'Giáo lý viên') === 'Tu Sĩ' ? 0 : 1;
      return aIsTuSi - bIsTuSi;
    });

    return matched.map(t => {
      const isTuSi = (t.religious || 'Giáo lý viên') === 'Tu Sĩ';
      const isFemale = (t.gender || 'Nam') === 'Nữ';

      let prefix = '';
      if (isTuSi) {
        prefix = isFemale ? 'Sr' : 'Thầy';
      } else {
        prefix = isFemale ? 'Chị' : 'Anh';
      }

      const holy = t.holyName ? `${t.holyName} ` : '';
      return `${prefix} ${holy}${t.fullName || ''}`.trim();
    });
  }, [currentClassName, activeClassId, catechistsData]);

  const sortedStudents = useMemo(() => {
    const rawList = studentsData[activeClassId] || [];

    const list = rawList.filter((student) => {
      const status = (student.status || '').trim().toLowerCase();
      return status !== 'nghỉ luôn';
    });

    const getLastName = (fullName) => {
      if (!fullName) return '';
      const parts = fullName.trim().split(/\s+/);
      return parts[parts.length - 1].toLowerCase();
    };

    list.sort((a, b) => {
      const lastNameA = getLastName(a.fullName);
      const lastNameB = getLastName(b.fullName);
      const cmp = lastNameA.localeCompare(lastNameB, 'vi');
      if (cmp !== 0) return cmp;
      return (a.fullName || '').localeCompare(b.fullName || '', 'vi');
    });

    return list;
  }, [studentsData, activeClassId]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="pcl-container">
      {/* THANH CÔNG CỤ ĐIỀU KHIỂN (ẨN KHI IN) */}
      <div className="pcl-toolbar no-print">
        <div className="pcl-toolbar-left">
          {loadingPrevYear && <span className="pcl-loading-text">⏳ Đang tải lớp cũ...</span>}
        </div>

        <button onClick={handlePrint} className="pcl-btn-print">
          🖨️ In Danh Sách Lớp (A4)
        </button>
      </div>

      {/* KHUNG TRANG IN CHUẨN A4 */}
      <div className="pcl-sheet">
        {/* PHẦN ĐẦU */}
        <div className="pcl-header-top">
          <div className="pcl-origin-left">
            <div className="pcl-parish-name">GIÁO XỨ AN NHƠN</div>
            <div className="pcl-group-name">XỨ ĐOÀN PHAOLÔ LÊ VĂN LỘC</div>
            <div className="pcl-header-divider"></div>
          </div>
        </div>

        {/* 🌟 PHẦN TIÊU ĐỀ: BỌC HAI DÒNG VÀO BANNER CÓ MÀU THEO NGÀNH */}
        <section className="pcl-title-section">
          <div className={`pcl-title-banner ${groupColorClass}`}>
            <h1 className="pcl-main-title">
              DANH SÁCH LỚP {groupName.toUpperCase().startsWith('NGÀNH') ? groupName.toUpperCase() : `NGÀNH ${groupName.toUpperCase()}`}
            </h1>
            <h2 className="pcl-class-title">LỚP {currentClassName.toUpperCase()}</h2>
          </div>
          <p className="pcl-year-text">Niên khóa: {currentYearName || 'Niên Khóa 2026 - 2027'}</p>
        </section>

        {/* PHẦN PHỤ */}
        <section className="pcl-meta-section">
          <div className="pcl-meta-item">
            <span className="pcl-meta-label">PHÒNG:</span>
            <span className="pcl-meta-value">{roomName}</span>
          </div>

          <div className="pcl-meta-item pcl-meta-teachers">
            <span className="pcl-meta-label">ĐỨNG LỚP:</span>
            <div className="pcl-teachers-list">
              {assignedTeachers.length > 0 ? (
                assignedTeachers.map((teacher, index) => (
                  <div key={index} className="pcl-teacher-line">
                    • {teacher}
                  </div>
                ))
              ) : (
                <div className="pcl-teacher-line">— Chưa có phân công</div>
              )}
            </div>
          </div>
        </section>

        {/* PHẦN CHÍNH: BẢNG DANH SÁCH LỚP */}
        <section className="pcl-table-section">
          <table className="pcl-table">
            <thead>
              <tr>
                <th className="pcl-col-stt">STT</th>
                <th className="pcl-col-id">Mã ID</th>
                <th className="pcl-col-holy">Tên Thánh</th>
                <th className="pcl-col-name">Họ và Tên</th>
                <th className="pcl-col-old-class">Lớp Cũ</th>
              </tr>
            </thead>
            <tbody>
              {sortedStudents.length > 0 ? (
                sortedStudents.map((student, idx) => (
                  <tr key={student.id || idx}>
                    <td className="pcl-text-center">{idx + 1}</td>
                    <td className="pcl-col-id pcl-text-center">{student.id}</td>
                    <td>{student.holyName || '-'}</td>
                    <td className="pcl-font-bold">{student.fullName}</td>
                    <td className="pcl-text-center">{prevClassMap[student.id] || 'Mới'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="pcl-text-center" style={{ padding: '24px', color: '#64748b' }}>
                    Chưa có học sinh nào trong lớp này.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
};

export default PrintClassList;